// Which lists the server has answered on this device, and which are still
// waiting for it (#651). The rule these serve is `core/src/reading.rs`'s
// `answer_is_read`, and its header is the argument; this is the web's half of
// the bookkeeping, as `ServerAnswers` is each phone's
// (android/app/src/main/kotlin/studio/coda/app/data/ServerAnswers.kt,
// ios/CodaStudio/Data/ServerAnswers.swift).
//
// ── The mark: the server has answered this list here before ─────────────────
//
// A listener's answer from the cache is a read only on a device that has had
// the server's answer for that list before — otherwise an empty cache is a
// device that never synced, and "You don't have any students yet" is a guess.
// Only something durable can say "before", and it must vouch for the cache
// that is answering:
//
//   * **The read-store arm** keeps it on the receipt (`confirmed`, in
//     src/data/appStore.js), because there the store is what answers, and the
//     receipt is already the row that says what the store has seen. It goes
//     when the store's rows go.
//   * **The snapshot arm** has no store of ours — the SDK's own cache answers —
//     so the mark is kept here, in `localStorage`, for the one studio whose
//     documents that cache holds. `AuthContext` drops the SDK's cache when a
//     different teacher signs in on this browser (`sweepSdkCache`), and calls
//     `forgetServerAnswers` with it: a mark that outlived the cache it vouched
//     for would make an empty cache read as a confirmed empty studio, which
//     is #651 again. One studio at a time, for the same reason — the sweep
//     means the cache holds one teacher's documents.
//
// `localStorage` because the question is answered synchronously while a
// snapshot is being handled, and a private window or blocked storage reads as
// "never answered here": the cost of that is the sentence below over a list
// the server has answered, until the server answers it again this session —
// the cautious direction.
//
// ── The registry: the lists whose only answer is such a cache ───────────────
//
// In memory, per tab, as `staleReads.js` is, and for its reason: the loading
// state that turns it into words is one component every screen draws through
// (`Unread`), and it cannot know which provider's list it stands for. So each
// collection window reports here, and `Unread` asks whether any list is
// waiting — the case it exists for is device-wide, a device that has heard
// nothing about any list.

const MARK_KEY = 'coda.serverAnswered';

function readMarks() {
  try {
    const raw = globalThis.localStorage?.getItem(MARK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.uid !== 'string' || !Array.isArray(parsed.collections)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Whether the server has answered `collection` for `uid` on this browser before (snapshot arm). */
export function serverAnsweredBefore(uid, collection) {
  const marks = readMarks();
  return Boolean(marks && marks.uid === uid && marks.collections.includes(collection));
}

/** The server has answered `collection` for `uid` (snapshot arm). */
export function rememberServerAnswered(uid, collection) {
  if (!uid) return;
  const marks = readMarks();
  const collections = marks && marks.uid === uid ? marks.collections : [];
  if (collections.includes(collection)) return;
  try {
    globalThis.localStorage?.setItem(
      MARK_KEY, JSON.stringify({ uid, collections: [...collections, collection] }),
    );
  } catch {
    // Private mode or a full quota: the list reads as never answered here,
    // the cautious direction (see the header).
  }
}

/** The SDK's cache went, so what it vouched for goes with it. */
export function forgetServerAnswers() {
  try { globalThis.localStorage?.removeItem(MARK_KEY); } catch { /* nothing stored to forget */ }
}

// ── The registry ────────────────────────────────────────────────────────────

const waiting = new Set();
const listeners = new Set();

function announce() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.error('[serverAnswers] a listener threw:', err);
    }
  });
}

/**
 * Report whether one scope's list is waiting for the server: answered only by
 * a cache that has never had the server's answer for it, and holding nothing.
 */
export function reportAwaiting(scope, isAwaiting) {
  if (waiting.has(scope) === Boolean(isAwaiting)) return;
  if (isAwaiting) waiting.add(scope);
  else waiting.delete(scope);
  announce();
}

/** A scope no longer read at all — an unmounted provider. */
export function forgetAwaiting(scope) {
  if (!waiting.delete(scope)) return;
  announce();
}

/** How many lists are waiting for the server. */
export function awaitingServerCount() {
  return waiting.size;
}

/** Subscribe to the count changing. Returns an unsubscribe. */
export function onAwaitingServerChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test seam. */
export function __resetServerAnswersForTests() {
  waiting.clear();
  listeners.clear();
  forgetServerAnswers();
}
